'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

interface RealTimeUpdateOptions<T> {
  interval?: number;
  enabled?: boolean;
  onUpdate?: (data: T) => void;
}

export function useRealTimeUpdates<T>(
  fetchFunction: () => Promise<T>,
  options: RealTimeUpdateOptions<T> = {}
) {
  const { interval = 30000, enabled = true, onUpdate } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchRef = useRef(fetchFunction);
  const onUpdateRef = useRef(onUpdate);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    fetchRef.current = fetchFunction;
    onUpdateRef.current = onUpdate;
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      setLoading(true);
      setError(null);
      const result = await fetchRef.current();
      if (!mountedRef.current) return;
      setData((prev) => (Object.is(prev, result) ? prev : result));
      setLastUpdate(new Date());
      onUpdateRef.current?.(result);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch updates');
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchData();
    const id = setInterval(fetchData, interval);
    return () => clearInterval(id);
  }, [interval, enabled, fetchData]);

  return { data, loading, error, lastUpdate, refresh: fetchData };
}

// Specific hooks for different data types
export function useDashboardStats() {
  return useRealTimeUpdates(async () => {
    const response = await fetch('/api/dashboard');
    if (!response.ok) throw new Error('Failed to fetch dashboard stats');
    return response.json();
  }, { interval: 60000 }); // Update every minute for dashboard
}

export function useCoatAvailability() {
  return useRealTimeUpdates(async () => {
    const response = await fetch('/api coats');
    if (!response.ok) throw new Error('Failed to fetch coat availability');
    return response.json();
  }, { interval: 30000 }); // Update every 30 seconds for coats
}

export function useOverdueRentals() {
  return useRealTimeUpdates(async () => {
    const response = await fetch('/api/rentals?status=overdue');
    if (!response.ok) throw new Error('Failed to fetch overdue rentals');
    return response.json();
  }, { interval: 45000 }); // Update every 45 seconds for overdue alerts
}
